// Forms API client (MOD-07, config-tier) — form tanımı + başvuru (submission) yönetimi.
//
// Uçlar `/api/v1/forms/...` altında (admin CRUD'da /admin segmenti YOK). Policy'ler:
// list/get → form.view, create/update/delete/publish → form.manage, submissions.* →
// form.submissions (PII ayrı izni). Enum'lar wire'da string. `fieldsJson` bir JSON
// string'tir → get parse eder, update serialize eder (bkz. utils/fields). Publish iki-durum
// toggle (POST /{id}/publish?publish=bool). Export ikili blob indirir (xlsx|pdf).

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import { parseFields, serializeFields } from '@features/forms/utils/fields';
import {
  normalizeConfigStatus,
  normalizeFormType,
  normalizeSubmissionStatus,
  type CreateFormRequest,
  type FormDetail,
  type FormDtoWire,
  type FormCreateInput,
  type FormListItem,
  type FormListItemDtoWire,
  type FormListQuery,
  type FormSubmissionDetail,
  type FormSubmissionDtoWire,
  type FormSubmissionListItem,
  type FormSubmissionListItemDtoWire,
  type FormUpdateInput,
  type SubmissionListQuery,
  type UpdateFormRequest,
} from '@features/forms/types';
import type { ExportFormat, SubmissionStatus } from '@features/forms/constants';

const BASE = '/api/v1/forms';

function mapListItem(d: FormListItemDtoWire): FormListItem {
  return {
    id: d.id ?? '',
    title: d.title ?? '',
    slug: d.slug ?? '',
    formType: normalizeFormType(d.formType),
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    submissionCount: d.submissionCount ?? 0,
    updatedAt: d.updatedAt ?? '',
  };
}

function mapDetail(d: FormDtoWire): FormDetail {
  return {
    id: d.id ?? '',
    title: d.title ?? '',
    slug: d.slug ?? '',
    formType: normalizeFormType(d.formType),
    fields: parseFields(d.fieldsJson),
    emailRecipients: d.emailRecipients ?? [],
    kvkkText: d.kvkkText ?? '',
    captchaEnabled: d.captchaEnabled ?? false,
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function mapSubmissionListItem(d: FormSubmissionListItemDtoWire): FormSubmissionListItem {
  return {
    id: d.id ?? '',
    formId: d.formId ?? '',
    status: normalizeSubmissionStatus(d.status),
    hasAttachment: d.hasAttachment ?? false,
    submittedAt: d.submittedAt ?? '',
  };
}

function mapSubmissionDetail(d: FormSubmissionDtoWire): FormSubmissionDetail {
  return {
    id: d.id ?? '',
    formId: d.formId ?? '',
    data: d.data ?? {},
    fileIds: d.fileIds ?? [],
    submitterIp: d.submitterIp ?? null,
    kvkkConsentAt: d.kvkkConsentAt ?? null,
    status: normalizeSubmissionStatus(d.status),
    submittedAt: d.submittedAt ?? '',
  };
}

export const formsApi = {
  async list(query: FormListQuery = {}): Promise<FormListItem[]> {
    const params: Record<string, string | number> = { page: query.page ?? 1, pageSize: query.pageSize ?? 200 };
    if (query.formType) params.formType = query.formType;
    if (query.languageId) params.languageId = query.languageId;
    const res = await http.get<ActionResponse<FormListItemDtoWire>>(BASE, { params });
    return unwrapList(res).items.map(mapListItem);
  },

  async get(id: string): Promise<FormDetail> {
    return mapDetail(unwrap(await http.get<ActionResponse<FormDtoWire>>(`${BASE}/${id}`)));
  },

  /** POST → tam FormDto döner (kabuk; alanlar builder'da doldurulur). */
  async create(input: FormCreateInput): Promise<FormDetail> {
    const body: CreateFormRequest = {
      title: input.title,
      formType: input.formType,
      ...(input.slug ? { slug: input.slug } : {}),
      ...(input.languageId ? { languageId: input.languageId } : {}),
    };
    return mapDetail(unwrap(await http.post<ActionResponse<FormDtoWire>>(BASE, body)));
  },

  /** PUT → Boolean; fieldsJson serialize edilir, güncel formu geri çeker. */
  async update(id: string, input: FormUpdateInput): Promise<FormDetail> {
    const body: UpdateFormRequest = {
      title: input.title,
      formType: input.formType,
      slug: input.slug,
      fieldsJson: serializeFields(input.fields),
      emailRecipients: input.emailRecipients,
      kvkkText: input.kvkkText,
      captchaEnabled: input.captchaEnabled,
    };
    unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body));
    return formsApi.get(id);
  },

  async remove(id: string): Promise<void> {
    unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`));
  },

  /** Publish toggle — iki-durum (Draft↔Published). */
  async setPublished(id: string, publish: boolean): Promise<void> {
    unwrap(
      await http.post<ActionResponse<boolean>>(`${BASE}/${id}/publish`, null, {
        params: { publish },
      }),
    );
  },

  submissions: {
    async list(formId: string, query: SubmissionListQuery = {}): Promise<{ items: FormSubmissionListItem[]; total: number; page: number; pageSize: number }> {
      const params: Record<string, string | number> = {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 20,
      };
      if (query.status) params.status = query.status;
      if (query.from) params.from = query.from;
      if (query.to) params.to = query.to;
      const res = await http.get<ActionResponse<FormSubmissionListItemDtoWire>>(
        `${BASE}/${formId}/submissions`,
        { params },
      );
      const { items, pagination } = unwrapList(res);
      return {
        items: items.map(mapSubmissionListItem),
        total: pagination?.totalCount ?? items.length,
        page: pagination?.page ?? query.page ?? 1,
        pageSize: pagination?.pageSize ?? query.pageSize ?? 20,
      };
    },

    async get(formId: string, submissionId: string): Promise<FormSubmissionDetail> {
      return mapSubmissionDetail(
        unwrap(
          await http.get<ActionResponse<FormSubmissionDtoWire>>(
            `${BASE}/${formId}/submissions/${submissionId}`,
          ),
        ),
      );
    },

    async setStatus(formId: string, submissionId: string, status: SubmissionStatus): Promise<void> {
      unwrap(
        await http.put<ActionResponse<boolean>>(
          `${BASE}/${formId}/submissions/${submissionId}/status`,
          { status },
        ),
      );
    },

    /**
     * Tek bir başvuru ekini (CV vb.) kimlik-doğrulamalı uçtan blob olarak indirir —
     * form.submissions izni ister. Dosya artık public URL'den DEĞİL Bearer token'lı bu
     * uçtan iner (public erişim kapalı). Emsal: `export` blob + filesApi.download.
     */
    async downloadFile(formId: string, submissionId: string, fileId: string): Promise<Blob> {
      const res = await http.get(
        `${BASE}/${formId}/submissions/${submissionId}/files/${fileId}`,
        { responseType: 'blob' },
      );
      return res.data as Blob;
    },

    /** İkili (xlsx|pdf) rapor blob'u — form.submissions izni ister; 400 EXPORT_TOO_LARGE olabilir. */
    async export(
      formId: string,
      query: { format: ExportFormat } & Omit<SubmissionListQuery, 'page' | 'pageSize'>,
    ): Promise<Blob> {
      const params: Record<string, string> = { format: query.format };
      if (query.status) params.status = query.status;
      if (query.from) params.from = query.from;
      if (query.to) params.to = query.to;
      const res = await http.get(`${BASE}/${formId}/submissions/export`, {
        params,
        responseType: 'blob',
      });
      return res.data as Blob;
    },
  },
};
